// src/portal-app/team/ScheduleScreen.js
//
// Plan 4 Task 8 — Schedule screen, migrated to the P4 pattern (spec §5).
// operations/TransactionsScreen.js:32-55 is the reference implementation;
// operations/ReservationsScreen.js (Task 7) is the closest reviewed model —
// both deal with time-based rows and a day-navigation control. This header
// records only what is specific to Schedule.
//
// ONE FilterRow (station) -> ONE FilterButton + FilterSheet. The old stats
// bar (three raw numbers: shifts / staff / hours) is replaced by a HeroStat
// card whose value is the shift count for the CURRENTLY FILTERED day —
// i.e. dayShifts.length, the exact same array rendered as CondensedRows
// below it. Staff-on-shift count and total scheduled hours (the two other
// stats bar numbers) are composed into the hero caption rather than dropped;
// see the "dropped fields" note in the task report.
//
// STATION COLOUR / TOKEN RESOLUTION. CLAUDE.md documents station colours as
// raw hex (Kitchen #d97706, Bar #2563eb, Cashier #198754, FOH #7c3aed,
// default #0891b2), but Global Constraints forbid raw hex in components.
// The kit's TONES vocabulary (shell/kit/tones.js) already carries four of
// these five values EXACTLY: amber=#d97706, blue=#2563eb, purple=#7c3aed,
// teal=#0891b2. Cashier's #198754 has NO exact token — the closest existing
// token is --c-success (#16a34a), which is what TONES.green already
// resolves to and is the kit's only "real green" (--c-green was repurposed
// to blue app-wide — see theme.css:31 "brand accent (was green -> blue)").
// Used tone='green' (-> --c-success) for Cashier rather than inventing a new
// token. Station is NEVER colour-only: the pill's LABEL is always the
// station name, so a colour-blind reader still gets the station from text.
//
// SEGMENTED CONTROL — not applicable. There is exactly one data source here
// (shiftService.getAllShifts()); the station axis is a plain client-side
// filter over that one array, so it belongs in FilterSheet, not a sibling
// SegmentedControl (the Task 4 Products defect this rule now guards
// against).
//
// EMPTY IS GENUINE, EVEN THOUGH FILTERING IS CLIENT-SIDE. Unlike Products
// (Task 4), where a client-side filter left `status` at 'ready' and the
// "filtered to empty" test proved nothing, this screen has always derived
// its SurfaceStates status from the POST-FILTER array:
//   surfaceStatus = status === 'ready' && dayShifts.length === 0 ? 'empty' : status
// A station filter that matches nothing on the selected day genuinely flips
// SurfaceStates into its empty early-return branch. That derivation is kept
// unchanged, so the "controls survive empty" test below exercises the real
// defect, not a proxy for it (see task report §Mutation evidence).
//
// ROW FIELDS — nothing operational dropped, only re-sited:
//   name       employee name
//   value      "{start} – {end}" time range (unchanged format)
//   pill       station name, toned per the mapping above (CondensedRow has
//              no node slot for a StatusBadge; the pill IS that slot)
//   secondary  "{status} · {expected}h · actual {actual}h" — status word,
//              expected hours and actual hours (when clocked), composed so
//              nothing behind the old StatusBadge/hours spans is lost.
//   icon       a clock glyph (CondensedRow's 32px icon slot renders blank,
//              wasting the space, when no icon prop is passed — see
//              TransactionsScreen.js's file header for the defect this
//              repeats).
//
// FETCH TIMING UNCHANGED. Employees + shifts still fetch once on mount
// (Promise.all), and after every create/update/delete/copy-week. No new
// fetch was added to feed the hero — it is a reduction over `shifts`, the
// same array the list already had.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, {
    useState, useEffect, useCallback, useRef,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { employeeService, shiftService } from '../../services/api';
import { SurfaceStates } from '../insights/kit';
import {
    CondensedRow,
    ConfirmSheet,
    FilterButton,
    FilterSheet,
    HeroStat,
    PullToRefresh,
    ensureKitCss,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';
import { useNavChrome } from '../nav/NavStack';

/* ------------------------------------------------------------------ */
/* Stylesheet                                                          */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.sch-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 120px;}

/* ---- hero ---- */
.sch-hero{padding:18px 20px 16px;margin:0 16px 12px;}

/* ---- week navigation ---- */
.sch-week-nav{display:flex;align-items:center;gap:8px;padding:0 16px 12px;
  flex-wrap:wrap;}
.sch-week-label{flex:1;font-size:13px;font-weight:700;color:var(--c-ink);
  text-align:center;letter-spacing:.2px;}
.sch-nav-btn{min-height:36px;min-width:36px;padding:0 10px;border-radius:12px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 40%,transparent);
  font-family:inherit;font-size:12px;font-weight:700;color:var(--c-ink);
  cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:4px;
  -webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);
  transition:transform .12s ease,background .15s ease;}
.sch-nav-btn:active{transform:scale(.96);}
.sch-today-btn{background:color-mix(in srgb,var(--c-green) 10%,transparent);
  border-color:color-mix(in srgb,var(--c-green) 35%,transparent);
  color:var(--c-green);}

/* ---- control row: day chips + FilterButton ---- */
.sch-control-row{display:flex;align-items:flex-start;gap:8px;margin:0 16px 10px;}
.sch-days-wrap{flex:1 1 auto;min-width:0;}
.sch-control-row > .pk-fb{flex:none;margin-block-start:2px;}

/* ---- day chips ---- */
.sch-days{display:flex;gap:8px;overflow-x:auto;padding:2px 0;
  -webkit-overflow-scrolling:touch;scrollbar-width:none;}
.sch-days::-webkit-scrollbar{display:none;}
.sch-day-chip{flex:none;display:inline-flex;flex-direction:column;align-items:center;
  justify-content:center;min-height:56px;min-width:52px;padding:6px 12px;
  border-radius:16px;cursor:pointer;font-family:inherit;white-space:nowrap;
  background:color-mix(in srgb,var(--c-surface) 38%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-surface) 55%,transparent);
  -webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);
  transition:transform .12s ease,background .15s ease,border-color .15s ease;}
.sch-day-chip:active{transform:scale(.96);}
.sch-day-chip-active{background:var(--c-green);border-color:var(--c-green);}
.sch-day-chip-label{font-size:11px;font-weight:700;letter-spacing:.2px;
  color:var(--c-textLight);}
.sch-day-chip-active .sch-day-chip-label{color:color-mix(in srgb,var(--c-white) 80%,transparent);}
.sch-day-chip-date{font-size:15px;font-weight:800;color:var(--c-ink);margin-top:1px;}
.sch-day-chip-active .sch-day-chip-date{color:var(--c-white);}
.sch-day-chip-dot{width:5px;height:5px;border-radius:50%;margin-top:3px;
  background:color-mix(in srgb,var(--c-ink) 25%,transparent);}
.sch-day-chip-active .sch-day-chip-dot{background:color-mix(in srgb,var(--c-white) 50%,transparent);}

/* ---- active-filter summary ---- */
.sch-filtersum{display:flex;align-items:center;gap:12px;margin:4px 16px 12px;}
.sch-filtersum-text{flex:1 1 auto;min-width:0;font-size:12px;font-weight:600;
  color:var(--c-textLight);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.sch-filtersum-clear{flex:none;min-height:44px;padding-inline:6px;border:none;
  background:transparent;font-family:inherit;font-size:12px;font-weight:700;
  color:var(--c-green);cursor:pointer;}

/* ---- count line + list ---- */
.sch-count-line{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin:2px 16px 10px;}
.sch-list{display:flex;flex-direction:column;gap:10px;padding:0 16px;}

/* ---- conflict banner (inside add/edit sheets) ---- */
.sch-conflict{padding:10px 14px;border-radius:12px;margin-bottom:12px;
  background:color-mix(in srgb,var(--c-amber) 10%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-amber) 30%,transparent);
  font-size:13px;font-weight:600;color:var(--c-amber);
  display:flex;align-items:center;gap:8px;}

/* ---- overtime banner ---- */
.sch-overtime{padding:10px 14px;border-radius:12px;margin:0 16px 12px;
  background:color-mix(in srgb,var(--c-amber) 10%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-amber) 30%,transparent);
  font-size:13px;font-weight:600;color:var(--c-amber);}

/* ---- FAB ---- */
.sch-fab{position:fixed;bottom:calc(80px + var(--safe-bottom,0px));
  inset-inline-end:20px;z-index:200;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  min-height:52px;min-width:52px;padding:0 20px;border-radius:26px;border:none;
  background:var(--c-green);
  font-family:inherit;font-size:14px;font-weight:700;color:var(--c-white);
  cursor:pointer;
  box-shadow:0 4px 20px color-mix(in srgb,var(--c-green) 40%,transparent);
  transition:transform .15s ease,box-shadow .15s ease;}
.sch-fab:active{transform:scale(.97);}

/* ---- sheet backdrop ---- */
.sch-backdrop{position:fixed;inset:0;z-index:1000;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.sch-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:24px 20px calc(24px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;
  max-height:85vh;overflow-y:auto;}
.sch-sheet-title{font-size:18px;font-weight:800;letter-spacing:-.3px;
  color:var(--c-ink);margin-bottom:16px;}
.sch-field{margin-bottom:14px;}
.sch-field-label{display:block;font-size:11px;font-weight:700;
  text-transform:uppercase;letter-spacing:.8px;color:var(--c-textLight);
  margin-bottom:5px;}
.sch-field-input,.sch-field-select{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding:10px 14px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  outline:none;}
.sch-field-input:focus,.sch-field-select:focus{border-color:var(--c-green);}
.sch-field-select{appearance:none;cursor:pointer;}
.sch-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.sch-error{padding:10px 14px;border-radius:12px;margin-bottom:12px;
  background:color-mix(in srgb,var(--c-red) 8%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-red) 25%,transparent);
  font-size:13px;font-weight:600;color:var(--c-red);}
.sch-actions{display:flex;gap:10px;margin-top:6px;}
.sch-btn-cancel{flex:1;min-height:44px;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:color-mix(in srgb,var(--c-surface) 50%,transparent);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;transition:transform .15s ease;}
.sch-btn-cancel:active{transform:scale(.97);}
.sch-btn-save{flex:1;min-height:44px;border:none;border-radius:16px;
  background:var(--c-green);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  transition:transform .15s ease,opacity .15s ease;}
.sch-btn-save:active{transform:scale(.97);}
.sch-btn-save:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.sch-btn-danger{flex:1;min-height:44px;border:none;border-radius:16px;
  background:color-mix(in srgb,var(--c-red) 8%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-red) 25%,transparent);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-red);cursor:pointer;transition:transform .15s ease;}
.sch-btn-danger:active{transform:scale(.97);}
.sch-btn-clock{min-height:44px;padding:0 18px;border:none;border-radius:16px;
  background:color-mix(in srgb,var(--c-blue) 10%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-blue) 25%,transparent);
  font-family:inherit;font-size:14px;font-weight:700;color:var(--c-blue);
  cursor:pointer;transition:transform .15s ease;}
.sch-btn-clock:active{transform:scale(.97);}
.sch-btn-clock:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.sch-divider{border:none;border-top:1px solid
  color-mix(in srgb,var(--c-ink) 8%,transparent);margin:14px 0;}
.sch-spinner{width:16px;height:16px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-white) 30%,transparent);
  border-top-color:var(--c-white);
  animation:schSpin .75s linear infinite;}
@keyframes schSpin{to{transform:rotate(360deg);}}

/* ---- slide-up ---- */
@keyframes schSlideUp{
  from{transform:translateY(100%);opacity:0;}
  to{transform:translateY(0);opacity:1;}
}
.sch-sheet-enter{animation:schSlideUp 260ms ease-out both;}
@keyframes schFadeIn{from{opacity:0;}to{opacity:1;}}
.sch-backdrop-enter{animation:schFadeIn 200ms ease both;}

@media(prefers-reduced-motion:reduce){
  .sch-sheet-enter,.sch-backdrop-enter{animation:none;transition:none;}
  .sch-spinner{animation-duration:1.5s;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'sch-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Week / day helpers (local time — never toISOString)                */
/* ------------------------------------------------------------------ */

/** Get ISO week number from a local Date (Monday-based). */
function getISOWeek(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayOfWeek = d.getDay() || 7; // Sunday = 7
    d.setDate(d.getDate() + 4 - dayOfWeek); // Nearest Thursday
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

/** Get the ISO year that contains the week (may differ in Jan/Dec). */
function getISOYear(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayOfWeek = d.getDay() || 7;
    d.setDate(d.getDate() + 4 - dayOfWeek);
    return d.getFullYear();
}

/** Get Monday of the week containing `date` (local). */
function getMondayOf(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dow = d.getDay() || 7;
    d.setDate(d.getDate() - (dow - 1));
    return d;
}

/** Add `n` days to a Date (local, returns new Date). */
function addDays(date, n) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    d.setDate(d.getDate() + n);
    return d;
}

/** Format a local Date as YYYY-MM-DD. */
function localIso(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/** Current local time as HH:MM string. */
function nowHHMM() {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

/** Convert HH:MM to total minutes. */
function toMins(hhmm) {
    if (!hhmm) return 0;
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
}

/** Check if two [start,end] ranges overlap (times as minutes). */
function timesOverlap(s1, e1, s2, e2) {
    // Treat midnight-crossing: if end <= start, extend by 24h
    const e1r = e1 <= s1 ? e1 + 1440 : e1;
    const e2r = e2 <= s2 ? e2 + 1440 : e2;
    return s1 < e2r && s2 < e1r;
}

/* ------------------------------------------------------------------ */
/* Station tone — see file header for the token-resolution reasoning.  */
/* ------------------------------------------------------------------ */
function stationTone(station) {
    const s = (station || '').toLowerCase();
    if (s === 'kitchen') return 'amber';
    if (s === 'bar') return 'blue';
    if (s === 'cashier') return 'green';
    if (s === 'foh' || s === 'front of house') return 'purple';
    return 'teal';
}

/* ------------------------------------------------------------------ */
/* SVG icons                                                           */
/* ------------------------------------------------------------------ */
const PlusIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const ChevronLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M15 18l-6-6 6-6" />
    </svg>
);

const ChevronRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 18l6-6-6-6" />
    </svg>
);

const CopyIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
);

const WarnIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

// Row icon — CondensedRow's 32px icon slot renders as a blank tinted square
// when no icon is passed (see TransactionsScreen.js file header for the
// defect this repeats). A shift's dominant fact is a block of time, so a
// clock glyph represents every row regardless of station.
const IconClock = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 3" />
    </svg>
);

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_KEYS = ['day_mon', 'day_tue', 'day_wed', 'day_thu', 'day_fri', 'day_sat', 'day_sun'];

/* ------------------------------------------------------------------ */
/* AddShiftSheet                                                       */
/* ------------------------------------------------------------------ */
function AddShiftSheet({ open, onClose, onSaved, employees, shifts, selectedDate, stations, t }) {
    const BLANK = { employee_id: '', station: '', start_time: '', end_time: '', expected_hours: '' };
    const [form, setForm] = useState(BLANK);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) { setForm(BLANK); setError(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;

    const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

    // Conflict detection
    const hasConflict = (() => {
        if (!form.employee_id || !form.start_time || !form.end_time) return false;
        const empId = Number(form.employee_id);
        const s = toMins(form.start_time);
        const e = toMins(form.end_time);
        return shifts.some(
            (sh) =>
                sh.employee_id === empId &&
                sh.date === selectedDate &&
                timesOverlap(s, e, toMins(sh.start_time), toMins(sh.end_time)),
        );
    })();

    const handleSubmit = async () => {
        if (!form.employee_id) { setError(t('employee_required', {}, 'Select an employee')); return; }
        setError('');
        setSaving(true);
        try {
            const hrs = form.expected_hours ? parseFloat(form.expected_hours) : undefined;
            await shiftService.createShift({
                employee_id: Number(form.employee_id),
                date: selectedDate,
                station: form.station || null,
                start_time: form.start_time || null,
                end_time: form.end_time || null,
                expected_hours: hrs,
            });
            setSaving(false);
            onSaved();
        } catch (err) {
            setSaving(false);
            setError(err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed'));
        }
    };

    const activeEmps = employees.filter((e) => (e.status || '').toLowerCase() === 'active');

    return (
        <div
            className="sch-backdrop sch-backdrop-enter"
            role="presentation"
            onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}
        >
            <GlassCard
                veil
                className="sch-sheet sch-sheet-enter"
                data-testid="shift-form-sheet"
                role="dialog"
                aria-modal="true"
                aria-label={t('add_shift', {}, 'Add Shift')}
            >
                <div className="sch-sheet-title">{t('add_shift', {}, 'Add Shift')}</div>

                {error && <div className="sch-error" data-testid="form-error">{error}</div>}
                {hasConflict && (
                    <div className="sch-conflict" data-testid="conflict-banner">
                        <WarnIcon />
                        {t('shift_conflict_warning', {}, 'This employee already has an overlapping shift on this day.')}
                    </div>
                )}

                {/* Employee */}
                <div className="sch-field">
                    <label className="sch-field-label">{t('employee', {}, 'Employee')} *</label>
                    <select
                        className="sch-field-select"
                        data-testid="form-employee"
                        value={form.employee_id}
                        onChange={(e) => set('employee_id', e.target.value)}
                    >
                        <option value="">{t('select_employee', {}, 'Select employee')}</option>
                        {activeEmps.map((emp) => (
                            <option key={emp.employee_id} value={emp.employee_id} data-testid="emp-option">
                                {emp.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Station */}
                <div className="sch-field">
                    <label className="sch-field-label">{t('station', {}, 'Station')}</label>
                    <select
                        className="sch-field-select"
                        data-testid="form-station"
                        value={form.station}
                        onChange={(e) => set('station', e.target.value)}
                    >
                        <option value="">{t('select_station', {}, '— Select —')}</option>
                        {stations.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                {/* Times */}
                <div className="sch-grid-2">
                    <div className="sch-field">
                        <label className="sch-field-label">{t('start_time', {}, 'Start')}</label>
                        <input
                            className="sch-field-input"
                            data-testid="form-start-time"
                            type="time"
                            value={form.start_time}
                            onChange={(e) => set('start_time', e.target.value)}
                        />
                    </div>
                    <div className="sch-field">
                        <label className="sch-field-label">{t('end_time', {}, 'End')}</label>
                        <input
                            className="sch-field-input"
                            data-testid="form-end-time"
                            type="time"
                            value={form.end_time}
                            onChange={(e) => set('end_time', e.target.value)}
                        />
                    </div>
                </div>

                {/* Expected hours */}
                <div className="sch-field">
                    <label className="sch-field-label">{t('expected_hours', {}, 'Expected Hours')}</label>
                    <input
                        className="sch-field-input"
                        data-testid="form-expected-hours"
                        type="number"
                        min="0"
                        max="24"
                        step="0.5"
                        value={form.expected_hours}
                        placeholder="8"
                        onChange={(e) => set('expected_hours', e.target.value)}
                    />
                </div>

                <div className="sch-actions">
                    <button type="button" className="sch-btn-cancel" onClick={onClose}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="sch-btn-save"
                        data-testid="form-submit"
                        disabled={saving}
                        onClick={handleSubmit}
                    >
                        {saving && <span className="sch-spinner" aria-hidden="true" />}
                        {saving ? t('saving', {}, 'Saving...') : t('save', {}, 'Save')}
                    </button>
                </div>
            </GlassCard>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* EditShiftSheet                                                      */
/* ------------------------------------------------------------------ */
function EditShiftSheet({ open, shift, employees, shifts, stations, onClose, onSaved, onDeleted, t }) {
    const empMap = Object.fromEntries((employees || []).map((e) => [e.employee_id, e.name]));
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [clockBusy, setClockBusy] = useState(false);
    const [error, setError] = useState('');
    const [showDelete, setShowDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (shift) {
            setForm({
                station: shift.station || '',
                start_time: shift.start_time || '',
                end_time: shift.end_time || '',
                expected_hours: shift.expected_hours != null ? String(shift.expected_hours) : '',
            });
            setError('');
            setShowDelete(false);
        }
    }, [shift]);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open || !shift || !form) return null;

    const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

    // Conflict detection for edits (exclude the current shift)
    const hasConflict = (() => {
        if (!form.start_time || !form.end_time) return false;
        const s = toMins(form.start_time);
        const e = toMins(form.end_time);
        return shifts.some(
            (sh) =>
                sh.shift_id !== shift.shift_id &&
                sh.employee_id === shift.employee_id &&
                sh.date === shift.date &&
                timesOverlap(s, e, toMins(sh.start_time), toMins(sh.end_time)),
        );
    })();

    const handleSave = async () => {
        setError('');
        setSaving(true);
        try {
            const hrs = form.expected_hours ? parseFloat(form.expected_hours) : shift.expected_hours;
            await shiftService.updateShift(shift.shift_id, {
                employee_id: shift.employee_id,
                date: shift.date,
                station: form.station || shift.station,
                start_time: form.start_time,
                end_time: form.end_time,
                expected_hours: hrs,
                status: shift.status,
            });
            setSaving(false);
            onSaved();
        } catch (err) {
            setSaving(false);
            setError(err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed'));
        }
    };

    const handleClockIn = async () => {
        setClockBusy(true);
        try {
            await shiftService.managerClockIn(shift.shift_id, nowHHMM());
            setClockBusy(false);
            onSaved();
        } catch {
            setClockBusy(false);
        }
    };

    const handleClockOut = async () => {
        setClockBusy(true);
        try {
            await shiftService.managerClockOut(shift.shift_id, nowHHMM());
            setClockBusy(false);
            onSaved();
        } catch {
            setClockBusy(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await shiftService.deleteShift(shift.shift_id);
            setDeleting(false);
            onDeleted();
        } catch {
            setDeleting(false);
        }
    };

    const isScheduled = (shift.status || '').toLowerCase() === 'scheduled';
    const isClockedIn = ['in progress', 'clocked in'].includes((shift.status || '').toLowerCase());
    const isCompleted = (shift.status || '').toLowerCase() === 'completed';

    const empName = empMap[shift.employee_id] || String(shift.employee_id);

    return (
        <>
            <div
                className="sch-backdrop sch-backdrop-enter"
                role="presentation"
                onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}
            >
                <GlassCard
                    veil
                    className="sch-sheet sch-sheet-enter"
                    data-testid="edit-shift-sheet"
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('edit_shift', {}, 'Edit Shift')}
                >
                    <div className="sch-sheet-title">{empName}</div>

                    {error && <div className="sch-error" data-testid="edit-error">{error}</div>}
                    {hasConflict && (
                        <div className="sch-conflict" data-testid="conflict-banner">
                            <WarnIcon />
                            {t('shift_conflict_warning', {}, 'This employee already has an overlapping shift on this day.')}
                        </div>
                    )}

                    {/* Station */}
                    <div className="sch-field">
                        <label className="sch-field-label">{t('station', {}, 'Station')}</label>
                        <select
                            className="sch-field-select"
                            data-testid="edit-station"
                            value={form.station}
                            onChange={(e) => set('station', e.target.value)}
                        >
                            <option value="">{t('select_station', {}, '— Select —')}</option>
                            {stations.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    {/* Times */}
                    <div className="sch-grid-2">
                        <div className="sch-field">
                            <label className="sch-field-label">{t('start_time', {}, 'Start')}</label>
                            <input
                                className="sch-field-input"
                                data-testid="edit-start-time"
                                type="time"
                                value={form.start_time}
                                onChange={(e) => set('start_time', e.target.value)}
                            />
                        </div>
                        <div className="sch-field">
                            <label className="sch-field-label">{t('end_time', {}, 'End')}</label>
                            <input
                                className="sch-field-input"
                                data-testid="edit-end-time"
                                type="time"
                                value={form.end_time}
                                onChange={(e) => set('end_time', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Expected hours */}
                    <div className="sch-field">
                        <label className="sch-field-label">{t('expected_hours', {}, 'Expected Hours')}</label>
                        <input
                            className="sch-field-input"
                            data-testid="edit-expected-hours"
                            type="number"
                            min="0"
                            max="24"
                            step="0.5"
                            value={form.expected_hours}
                            onChange={(e) => set('expected_hours', e.target.value)}
                        />
                    </div>

                    {/* Clock in/out (only for relevant statuses) */}
                    {(isScheduled || isClockedIn) && !isCompleted && (
                        <>
                            <hr className="sch-divider" />
                            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                                {isScheduled && (
                                    <button
                                        type="button"
                                        className="sch-btn-clock"
                                        data-testid="clock-in-btn"
                                        disabled={clockBusy}
                                        onClick={handleClockIn}
                                    >
                                        {t('clock_in', {}, 'Clock In')}
                                    </button>
                                )}
                                {isClockedIn && (
                                    <button
                                        type="button"
                                        className="sch-btn-clock"
                                        data-testid="clock-out-btn"
                                        disabled={clockBusy}
                                        onClick={handleClockOut}
                                    >
                                        {t('clock_out', {}, 'Clock Out')}
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    <hr className="sch-divider" />

                    <div className="sch-actions">
                        <button
                            type="button"
                            className="sch-btn-danger"
                            data-testid="delete-shift-btn"
                            onClick={() => setShowDelete(true)}
                        >
                            {t('delete', {}, 'Delete')}
                        </button>
                        <button type="button" className="sch-btn-cancel" onClick={onClose}>
                            {t('cancel', {}, 'Cancel')}
                        </button>
                        <button
                            type="button"
                            className="sch-btn-save"
                            data-testid="edit-submit"
                            disabled={saving}
                            onClick={handleSave}
                        >
                            {saving && <span className="sch-spinner" aria-hidden="true" />}
                            {saving ? t('saving', {}, 'Saving...') : t('save', {}, 'Save')}
                        </button>
                    </div>
                </GlassCard>
            </div>

            <ConfirmSheet
                open={showDelete}
                title={t('delete_shift', {}, 'Delete Shift')}
                message={t('delete_shift_msg', {}, 'This shift will be permanently deleted.')}
                confirmLabel={t('delete', {}, 'Delete')}
                destructive
                loading={deleting}
                onClose={() => setShowDelete(false)}
                onConfirm={handleDelete}
            />
        </>
    );
}

/* ------------------------------------------------------------------ */
/* ScheduleRow — CondensedRow. See file header for the field mapping.  */
/* ------------------------------------------------------------------ */
function ScheduleRow({ shift, empName, onPress, t }) {
    const tone = shift.station ? stationTone(shift.station) : 'blue';
    const statusKey = (shift.status || '').toLowerCase().replace(/ /g, '_');
    const statusText = statusKey
        ? t(`shift_status_${statusKey}`, {}, shift.status || '')
        : null;
    const hoursText = shift.expected_hours != null
        ? `${shift.expected_hours}${t('hrs_label', {}, 'h')}`
        : null;
    const actualText = shift.actual_hours != null
        ? `${t('actual_label', {}, 'actual')} ${shift.actual_hours}${t('hrs_label', {}, 'h')}`
        : null;
    const secondary = [statusText, hoursText, actualText].filter(Boolean).join(' · ');

    return (
        <CondensedRow
            icon={<IconClock />}
            tone={tone}
            name={empName}
            value={`${shift.start_time || '—'} – ${shift.end_time || '—'}`}
            secondary={secondary || undefined}
            pill={shift.station ? { label: shift.station, tone } : undefined}
            onPress={onPress}
            testId={shift.shift_id}
        />
    );
}

/* ------------------------------------------------------------------ */
/* ScheduleScreen                                                      */
/* ------------------------------------------------------------------ */
export default function ScheduleScreen({ initialDate } = {}) {
    ensureCss();
    ensureKitCss();
    const { getTenantSetting, tenantConfig } = useAuth();
    const { t, isRTL } = useLanguage();

    // Week state — anchor is the Monday of the displayed week.
    // `initialDate` is an escape hatch for tests (never used in production).
    const baseDate = initialDate ? new Date(initialDate + 'T00:00:00') : new Date();
    const [weekMonday, setWeekMonday] = useState(() => getMondayOf(baseDate));
    const [selectedDate, setSelectedDate] = useState(() => localIso(baseDate));

    // Data
    const [employees, setEmployees] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [status, setStatus] = useState('loading');

    // UI state
    const [stationFilter, setStationFilter] = useState(null);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [editShift, setEditShift] = useState(null);
    const [showCopyWeek, setShowCopyWeek] = useState(false);
    const [copyBusy, setCopyBusy] = useState(false);

    // Stations from tenant config
    const stations = tenantConfig?.stations ||
        (getTenantSetting ? getTenantSetting('stations', ['Kitchen', 'Bar', 'Cashier', 'FOH']) : ['Kitchen', 'Bar', 'Cashier', 'FOH']);

    const fetchRef = useRef(0);

    const load = useCallback(async () => {
        const id = ++fetchRef.current;
        setStatus('loading');
        try {
            const [emps, allShifts] = await Promise.all([
                employeeService.getAllEmployees(),
                shiftService.getAllShifts(),
            ]);
            if (id !== fetchRef.current) return;
            setEmployees(Array.isArray(emps) ? emps : []);
            setShifts(Array.isArray(allShifts) ? allShifts : []);
            setStatus('ready');
        } catch {
            if (id !== fetchRef.current) return;
            setStatus('error');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Week navigation
    const prevWeek = () => {
        setWeekMonday((m) => addDays(m, -7));
        // Keep selected date within new week
        setSelectedDate((d) => {
            const newMon = addDays(getMondayOf(new Date(d)), -7);
            return localIso(newMon);
        });
    };
    const nextWeek = () => {
        setWeekMonday((m) => addDays(m, 7));
        setSelectedDate((d) => {
            const newMon = addDays(getMondayOf(new Date(d)), 7);
            return localIso(newMon);
        });
    };
    const goToday = () => {
        const today = new Date();
        setWeekMonday(getMondayOf(today));
        setSelectedDate(localIso(today));
    };

    // Day chips for current week (Mon–Sun)
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekMonday, i));

    // Current week/year info for copy-week
    const currentWeek = getISOWeek(weekMonday);
    const currentYear = getISOYear(weekMonday);
    const nextWeekNum = getISOWeek(addDays(weekMonday, 7));
    const nextYearNum = getISOYear(addDays(weekMonday, 7));

    // Shifts for selected day, filtered by station — this is the SAME array
    // rendered below AND reduced into the hero, so hero and rows always
    // share one denominator.
    const dayShifts = shifts.filter(
        (sh) =>
            sh.date === selectedDate &&
            (stationFilter === null || sh.station === stationFilter),
    );
    const heroShiftCount = dayShifts.length;
    const heroStaffCount = new Set(dayShifts.map((sh) => sh.employee_id)).size;
    const heroHours = dayShifts.reduce((sum, sh) => sum + (sh.expected_hours || 0), 0);

    // Overtime check across the whole displayed week (independent of the
    // day/station selection — it is a week-level payroll warning).
    const overtimeThreshold = getTenantSetting
        ? getTenantSetting('payroll.overtime_threshold_hours_week', 40)
        : 40;
    const weekShifts = shifts.filter((sh) => {
        const d = new Date(sh.date + 'T00:00:00');
        return d >= weekMonday && d <= addDays(weekMonday, 6);
    });
    const hoursPerEmployee = {};
    weekShifts.forEach((sh) => {
        hoursPerEmployee[sh.employee_id] = (hoursPerEmployee[sh.employee_id] || 0) + (sh.expected_hours || 0);
    });
    const hasOvertime = Object.values(hoursPerEmployee).some((h) => h > overtimeThreshold);

    // Surface status: empty if ready but no shifts for day (post-filter) —
    // a genuine derived state, not a client-side cosmetic filter (see file
    // header). Error/loading pass through untouched.
    const surfaceStatus = status === 'ready' && dayShifts.length === 0 ? 'empty' : status;

    // Employee name lookup
    const empMap = Object.fromEntries(employees.map((e) => [e.employee_id, e.name]));

    // Register copy-week in nav chrome
    useNavChrome({
        actions: [
            {
                id: 'copy-week',
                label: t('copy_week', {}, 'Copy Week'),
                icon: <CopyIcon />,
                onPress: () => setShowCopyWeek(true),
            },
        ],
    }, [t]);

    const handleCopyWeek = async () => {
        setCopyBusy(true);
        try {
            await shiftService.copyWeek(currentWeek, nextWeekNum, currentYear, nextYearNum);
            setCopyBusy(false);
            setShowCopyWeek(false);
            load();
        } catch {
            setCopyBusy(false);
            setShowCopyWeek(false);
        }
    };

    // Station FilterSheet — the one categorical filter on this surface.
    const stationOptions = stations.map((s) => ({ id: s, label: s }));
    const filterGroups = stationOptions.length > 0
        ? [{ id: 'station', label: t('station', {}, 'Station'), options: stationOptions }]
        : [];
    const filterValue = { station: stationFilter };
    const activeFilterCount = stationFilter != null ? 1 : 0;

    const handleFilterChange = (groupId, optionId) => {
        if (groupId === 'station') setStationFilter(optionId);
    };
    const clearAllFilters = () => setStationFilter(null);

    // Pull-to-refresh suppressed while any sheet owns the screen.
    const ptrDisabled = showAdd || editShift !== null || showCopyWeek || filterSheetOpen;

    const heroCaption = t(
        'staff_on_shift_hours',
        { staff: heroStaffCount, hours: heroHours },
        '{staff} staff on shift · {hours}h scheduled',
    );

    return (
        <div
            className="sch-wrap"
            dir={isRTL ? 'rtl' : 'ltr'}
            data-testid="schedule-screen"
        >
            {/* Hero — shift count for the selected (filtered) day; caption
                carries staff-on-shift + total scheduled hours. OUTSIDE
                SurfaceStates so it never vanishes with the list. */}
            <GlassCard veil className="sch-hero" data-testid="schedule-hero">
                <HeroStat
                    label={t('shifts_scheduled', {}, 'Shifts Scheduled')}
                    value={heroShiftCount}
                    caption={heroCaption}
                />
            </GlassCard>

            {/* Week navigation */}
            <div className="sch-week-nav">
                <button
                    type="button"
                    className="sch-nav-btn"
                    data-testid="prev-week"
                    aria-label={t('previous_week', {}, 'Previous week')}
                    onClick={prevWeek}
                >
                    <ChevronLeftIcon />
                </button>
                <span className="sch-week-label" data-testid="week-label">
                    {t('week_label', { n: currentWeek }, `Week ${currentWeek}`)}
                </span>
                <button
                    type="button"
                    className="sch-nav-btn"
                    data-testid="next-week"
                    aria-label={t('next_week', {}, 'Next week')}
                    onClick={nextWeek}
                >
                    <ChevronRightIcon />
                </button>
                <button
                    type="button"
                    className="sch-nav-btn sch-today-btn"
                    data-testid="today-btn"
                    onClick={goToday}
                >
                    {t('today', {}, 'Today')}
                </button>
            </div>

            {/* Control row — day chips (leading) + FilterButton (trailing).
                Both OUTSIDE SurfaceStates: a station filter that empties the
                day must not unmount the means to undo it. */}
            <div className="sch-control-row">
                <div
                    className="sch-days sch-days-wrap"
                    role="tablist"
                    aria-label={t('select_day', {}, 'Select day')}
                >
                    {weekDays.map((d, i) => {
                        const iso = localIso(d);
                        const isActive = iso === selectedDate;
                        const dayShiftCount = shifts.filter((sh) => sh.date === iso).length;
                        return (
                            <button
                                key={iso}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                className={`sch-day-chip${isActive ? ' sch-day-chip-active' : ''}`}
                                data-testid="day-chip"
                                data-active={isActive ? 'true' : 'false'}
                                data-date={iso}
                                onClick={() => setSelectedDate(iso)}
                            >
                                <span className="sch-day-chip-label">
                                    {t(DAY_KEYS[i], {}, DAY_NAMES[i])}
                                </span>
                                <span className="sch-day-chip-date">{d.getDate()}</span>
                                {dayShiftCount > 0 && <span className="sch-day-chip-dot" aria-hidden="true" />}
                            </button>
                        );
                    })}
                </div>
                <FilterButton
                    activeCount={activeFilterCount}
                    onPress={() => setFilterSheetOpen(true)}
                />
            </div>

            {/* Active-filter summary — OUTSIDE SurfaceStates for the same
                reason. */}
            {activeFilterCount > 0 && (
                <div className="sch-filtersum">
                    <span className="sch-filtersum-text">{stationFilter}</span>
                    <button
                        type="button"
                        className="sch-filtersum-clear"
                        data-testid="clear-filters"
                        onClick={clearAllFilters}
                    >
                        {t('clear', {}, 'Clear')}
                    </button>
                </div>
            )}

            {/* Overtime banner — week-level warning, independent of the day/
                station selection. */}
            {hasOvertime && (
                <div className="sch-overtime" data-testid="overtime-banner">
                    {t('overtime_warning', {}, 'Some staff are scheduled for overtime this week.')}
                </div>
            )}

            {/* Shift list */}
            <PullToRefresh onRefresh={load} disabled={ptrDisabled}>
                <SurfaceStates
                    status={surfaceStatus}
                    onRetry={load}
                    emptyTitle={t('no_shifts_today', {}, 'No shifts scheduled')}
                    emptyMsg={t('no_shifts_today_msg', {}, 'Add a shift to get started.')}
                >
                    <div className="sch-count-line" data-testid="shift-count-line">
                        {t('n_shifts', { count: dayShifts.length }, '{count} Shifts')}
                    </div>

                    <div className="sch-list" data-testid="shift-list">
                        {dayShifts.map((shift) => {
                            const empName = empMap[shift.employee_id] || String(shift.employee_id);
                            return (
                                <ScheduleRow
                                    key={shift.shift_id}
                                    shift={shift}
                                    empName={empName}
                                    onPress={() => setEditShift(shift)}
                                    t={t}
                                />
                            );
                        })}
                    </div>
                </SurfaceStates>
            </PullToRefresh>

            {/* FAB */}
            <button
                type="button"
                className="sch-fab"
                data-testid="add-shift-fab"
                aria-label={t('add_shift', {}, 'Add Shift')}
                onClick={() => setShowAdd(true)}
            >
                <PlusIcon />
                {t('add_shift', {}, 'Add Shift')}
            </button>

            {/* Add shift sheet */}
            <AddShiftSheet
                open={showAdd}
                onClose={() => setShowAdd(false)}
                onSaved={() => { setShowAdd(false); load(); }}
                employees={employees}
                shifts={shifts}
                selectedDate={selectedDate}
                stations={stations}
                t={t}
            />

            {/* Edit shift sheet */}
            <EditShiftSheet
                open={editShift !== null}
                shift={editShift}
                employees={employees}
                shifts={shifts}
                stations={stations}
                onClose={() => setEditShift(null)}
                onSaved={() => { setEditShift(null); load(); }}
                onDeleted={() => { setEditShift(null); load(); }}
                t={t}
            />

            {/* Copy week confirm */}
            <ConfirmSheet
                open={showCopyWeek}
                title={t('copy_week', {}, 'Copy Week')}
                message={t('copy_week_msg', {}, `Copy shifts from week ${currentWeek} to week ${nextWeekNum}?`)}
                confirmLabel={t('copy', {}, 'Copy')}
                loading={copyBusy}
                onClose={() => setShowCopyWeek(false)}
                onConfirm={handleCopyWeek}
            />

            {/* Filter sheet — station */}
            <FilterSheet
                open={filterSheetOpen}
                onClose={() => setFilterSheetOpen(false)}
                groups={filterGroups}
                value={filterValue}
                onChange={handleFilterChange}
                onClear={clearAllFilters}
            />
        </div>
    );
}
